import { h, mount } from '../lib/dom.js';
import { apiGet } from '../lib/api.js';
import { renderShell, pageHead, loading } from './shell.js';
import { fmtDateRange, fmtDate, fmtDuration, dayName, fmtNet, taskTypeLabel, taskTypeIcon } from '../lib/format.js';

/**
 * Haftalık rapor sayfası — koç ve öğrenci panellerinde ortak.
 *
 * @param outlet         — #app
 * @param weekId         — hafta id
 * @param backHref       — geri butonunun yönlendireceği path (koç: '/coach/weeks/:id', öğrenci: '/student/weeks/:id')
 * @param titlePrefix    — "Haftalık Rapor" (varsayılan) veya özel başlık
 * @param audience       — 'coach' | 'student' — küçük dil farkları için
 */
export async function renderWeekReport({ outlet, weekId, backHref, audience = 'coach' } = {}) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());

  const rep = await apiGet('/weeks/' + weekId + '/report');
  container.replaceChildren();

  container.appendChild(pageHead(
    'Haftalık Rapor',
    (rep.week.title ? rep.week.title + ' · ' : '') + fmtDateRange(rep.week.start_date, rep.week.end_date),
    backHref ? h('a', { class: 'btn btn-secondary', href: '#' + backHref }, '← Haftaya dön') : null,
  ));

  const s = rep.summary;

  // Motivasyon şeridi — öğrenci için
  if (audience === 'student') {
    container.appendChild(renderMotivationStrip(s));
  }

  container.appendChild(h('div', { class: 'grid grid-3' },
    h('div', { class: 'stat' },
      h('span', { class: 'stat-label' }, 'Tamamlama'),
      h('span', { class: 'stat-value' }, s.completion_rate + '%'),
      h('span', { class: 'stat-sub' }, s.counts.completed + ' / ' + s.counts.total + ' görev'),
    ),
    h('div', { class: 'stat' },
      h('span', { class: 'stat-label' }, 'Gerçekleşen süre'),
      h('span', { class: 'stat-value' }, fmtDuration(s.actual_minutes)),
      h('span', { class: 'stat-sub' }, 'Plan: ' + fmtDuration(s.planned_minutes) + ' · Hedef: ' + fmtDuration(s.target_minutes)),
    ),
    h('div', { class: 'stat' },
      h('span', { class: 'stat-label' }, 'Sorular'),
      h('span', { class: 'stat-value' }, s.question_actual + ''),
      h('span', { class: 'stat-sub' }, s.question_target ? 'Hedef: ' + s.question_target : '—'),
    ),
    h('div', { class: 'stat' },
      h('span', { class: 'stat-label' }, 'Doğru / Yanlış / Boş'),
      h('span', { class: 'stat-value' }, (s.question_correct || 0) + ' / ' + (s.question_incorrect || 0) + ' / ' + (s.question_blank || 0)),
      h('span', { class: 'stat-sub' }, 'Ham sayı'),
    ),
    h('div', { class: 'stat' },
      h('span', { class: 'stat-label' }, 'Net (YKS)'),
      h('span', { class: 'stat-value' }, (s.net ?? 0) + ''),
      h('span', { class: 'stat-sub' }, 'D − 0.25·Y'),
    ),
    h('div', { class: 'stat' },
      h('span', { class: 'stat-label' }, 'Ertelenen'),
      h('span', { class: 'stat-value small' }, s.counts.postponed + ' görev'),
      h('span', { class: 'stat-sub' }, s.most_postponed_subject ? s.most_postponed_subject.name : '—'),
    ),
    h('div', { class: 'stat' },
      h('span', { class: 'stat-label' }, 'Yapılmadı'),
      h('span', { class: 'stat-value small' }, s.counts.failed + ' görev'),
    ),
    h('div', { class: 'stat' },
      h('span', { class: 'stat-label' }, 'En çok çalışılan'),
      h('span', { class: 'stat-value small' }, s.most_worked_subject?.name || '—'),
      h('span', { class: 'stat-sub' }, s.most_worked_subject ? fmtDuration(s.most_worked_subject.actual_minutes) : ''),
    ),
  ));

  container.appendChild(renderPerType(rep.per_type));
  container.appendChild(renderPerSubject(rep.per_subject));
  container.appendChild(renderPerDay(rep.per_day));
}

function renderPerType(perType) {
  if (!perType || perType.length === 0) return h('div');
  const anyActivity = perType.some(t => t.total > 0);
  if (!anyActivity) return h('div');
  return h('section', { style: { marginTop: 'var(--sp-6)' } },
    h('div', { class: 'section-header' }, h('h3', {}, 'Görev türü dağılımı')),
    h('div', { class: 'type-cards' },
      ...perType.map(t => h('div', { class: 'type-card tt-' + t.type },
        h('span', { class: 'type-card-icon' }, taskTypeIcon(t.type)),
        h('div', { class: 'type-card-body' },
          h('div', { class: 'type-card-label' }, taskTypeLabel(t.type)),
          h('div', { class: 'type-card-value' }, fmtDuration(t.actual_minutes)),
          h('div', { class: 'type-card-sub' }, t.completed + ' / ' + t.total + ' görev'),
        ),
      )),
    ),
  );
}

function renderMotivationStrip(s) {
  const rate = s.completion_rate;
  const line = rate >= 90 ? { emoji: '🌿', label: 'Mükemmel bir hafta', body: 'Neredeyse hepsini bitirmişsin — bu ritim sürerse büyük fark yaratır.' }
    : rate >= 70 ? { emoji: '☀', label: 'Sağlam bir tempo', body: 'Çoğunu bitirdin. Kalan az iş: bir sonraki hafta daha da hafif başlayacaksın.' }
    : rate >= 50 ? { emoji: '·', label: 'Orta yolda', body: 'Yarıyı geçtin. Bu haftanın son adımlarında biraz daha çaba büyük fark yaratır.' }
    : rate >= 20 ? { emoji: '·', label: 'Yavaş başlangıç', body: 'Küçük görevleri işaretlemek bile ivme kazandırır. Bir sonraki güne odaklan.' }
    : { emoji: '·', label: 'Yeniden başlama zamanı', body: 'Küçük bir görevle başla — bir gün içinde eski ritmini bulmak mümkün.' };
  return h('div', { class: 'motivation-strip' },
    h('span', { class: 'motivation-emoji', 'aria-hidden': 'true' }, line.emoji),
    h('div', {},
      h('div', { class: 'motivation-label' }, line.label),
      h('p', { class: 'motivation-body' }, line.body),
    ),
  );
}

function renderPerSubject(perSubject) {
  if (!perSubject || perSubject.length === 0) return h('div');
  return h('section', { style: { marginTop: 'var(--sp-6)' } },
    h('div', { class: 'section-header' }, h('h3', {}, 'Ders bazlı çalışma')),
    h('div', { class: 'table-wrap' },
      h('table', { class: 'table' },
        h('thead', {}, h('tr', {},
          h('th', {}, 'Ders'),
          h('th', {}, 'Görev'),
          h('th', {}, 'Tamamlanan'),
          h('th', {}, 'Ertelenen'),
          h('th', {}, 'Plan Süre'),
          h('th', {}, 'Gerçekleşen'),
          h('th', {}, 'Soru'),
          h('th', {}, 'D'),
          h('th', {}, 'Y'),
          h('th', {}, 'B'),
          h('th', {}, 'Net'),
        )),
        h('tbody', {}, perSubject.map(s => h('tr', {},
          h('td', {}, h('span', { class: 'subject-swatch', style: { '--sw': s.subject_color || 'var(--color-border-strong)' } }), s.subject_name),
          h('td', {}, s.total + ''),
          h('td', {}, s.completed + ''),
          h('td', { class: s.postponed > 0 ? 'muted' : '' }, s.postponed + ''),
          h('td', {}, fmtDuration(s.planned_minutes)),
          h('td', {}, fmtDuration(s.actual_minutes)),
          h('td', {}, (s.question_actual || 0) + ''),
          h('td', {}, (s.question_correct || 0) + ''),
          h('td', {}, (s.question_incorrect || 0) + ''),
          h('td', {}, (s.question_blank || 0) + ''),
          h('td', {}, (s.net ?? 0) + ''),
        ))),
      ),
    ),
  );
}

function renderPerDay(perDay) {
  if (!perDay || perDay.length === 0) return h('div');
  return h('section', { style: { marginTop: 'var(--sp-6)' } },
    h('div', { class: 'section-header' }, h('h3', {}, 'Gün gün')),
    h('div', { class: 'table-wrap' },
      h('table', { class: 'table' },
        h('thead', {}, h('tr', {},
          h('th', {}, 'Gün'),
          h('th', {}, 'Görev'),
          h('th', {}, 'Tamamlanan'),
          h('th', {}, 'Plan Süre'),
          h('th', {}, 'Gerçekleşen'),
        )),
        h('tbody', {}, perDay.map(d => h('tr', {},
          h('td', {}, dayName(d.date, true) + ', ' + fmtDate(d.date, { short: true })),
          h('td', {}, d.total + ''),
          h('td', {}, d.completed + ''),
          h('td', {}, fmtDuration(d.planned_minutes)),
          h('td', {}, fmtDuration(d.actual_minutes)),
        ))),
      ),
    ),
  );
}
